// Вкладка «Предметы»: откуда каждый предмет попадает к игроку и с каким
// шансом. Формулы — в lib/items.ts; здесь только показ и ручки.
//
// ПРИЛОЖЕНИЕ НЕ ПИШЕТ В .asset (ADR-0023). Покрученные числа живут в памяти
// вкладки и уходят человеку списком «ассет · поле · было · стало» — перенос
// в инспекторе руками.

import { Fragment, useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { Callout, CopyButton, OriginTag, Panel, Tile } from '../components/Ui';
import { useStore } from '../data/store';
import { loadItems } from '../data/source';
import { num, percent, stamp } from '../lib/format';
import {
  TABLE_STATUS_TEXT,
  applyEdits,
  budgetFieldKey,
  budgetRowKey,
  changeList,
  enemyKey,
  formatChanges,
  listKnobs,
  summarizeAll,
  tableKey,
  validateModel,
  type Edits,
  type Estimate,
  type ItemSummary,
  type ItemsPayload,
  type Knob,
} from '../lib/items';

const EDITS_STORAGE = 'spusk.items.edits';

function readStoredEdits(): Record<string, number> {
  try {
    const raw = window.sessionStorage.getItem(EDITS_STORAGE);
    const parsed: unknown = raw === null ? {} : JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object') return {};
    const out: Record<string, number> = {};
    for (const [key, value] of Object.entries(parsed)) if (typeof value === 'number') out[key] = value;
    return out;
  } catch {
    return {};
  }
}

function storeEdits(edits: Edits) {
  try {
    window.sessionStorage.setItem(EDITS_STORAGE, JSON.stringify(edits));
  } catch {
    /* приватное окно — правки просто не переживут перезагрузку */
  }
}

/** Ожидание одним числом с диапазоном, если он есть. */
function est(value: Estimate | null, digits = 2): string {
  if (value === null) return '—';
  if (value.low === value.high) return num(value.mid, digits);
  return `${num(value.mid, digits)} (${num(value.low, digits)}–${num(value.high, digits)})`;
}

function KnobInput({ knob, edits, onEdit }: { knob: Knob; edits: Edits; onEdit: (key: string, value: number | null) => void }) {
  const value = knob.key in edits ? (edits[knob.key] as number) : knob.base;
  const changed = value !== knob.base;
  return (
    <label className={changed ? 'knob changed' : 'knob'} title={`${knob.asset.name} · ${knob.field} · было ${knob.base}`}>
      <span className="knob-label">{knob.label}</span>
      <input
        type="number"
        min={knob.min}
        max={knob.max}
        step={1}
        value={value}
        onChange={(event) => {
          const next = Number(event.target.value);
          if (!Number.isFinite(next)) return;
          const clamped = Math.max(knob.min, Math.min(knob.max, Math.round(next)));
          onEdit(knob.key, clamped === knob.base ? null : clamped);
        }}
      />
      {changed && <span className="faint">← {num(knob.base)}</span>}
    </label>
  );
}

export function Items() {
  const store = useStore();
  const [model, setModel] = useState<ItemsPayload | null>(null);
  const [origin, setOrigin] = useState<'server' | 'sample'>('server');
  const [failure, setFailure] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState<Record<string, number>>(readStoredEdits);
  const [query, setQuery] = useState('');
  const [showDisabled, setShowDisabled] = useState(false);
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(
    (refresh: boolean) => {
      if (store.server === null) return () => {};
      let alive = true;
      setLoading(true);
      void loadItems(store.server.online, refresh).then((result) => {
        if (!alive) return;
        setModel(result.value);
        setOrigin(result.origin);
        setFailure(result.fallbackReason);
        setLoading(false);
      });
      return () => {
        alive = false;
      };
    },
    [store.server],
  );

  useEffect(() => load(false), [load]);
  useEffect(() => storeEdits(edits), [edits]);

  const onEdit = useCallback((key: string, value: number | null) => {
    setEdits((current) => {
      const next = { ...current };
      if (value === null) delete next[key];
      else next[key] = value;
      return next;
    });
  }, []);

  const knobs = useMemo(() => (model === null ? [] : listKnobs(model)), [model]);
  const knobByKey = useMemo(() => new Map(knobs.map((knob) => [knob.key, knob])), [knobs]);
  const edited = useMemo(() => (model === null ? null : applyEdits(model, edits)), [model, edits]);
  const summaries = useMemo(() => (edited === null ? [] : summarizeAll(edited)), [edited]);
  const changes = useMemo(() => changeList(knobs, edits), [knobs, edits]);
  const problems = useMemo(() => (edited === null ? [] : validateModel(edited)), [edited]);

  const combat = summaries.filter((s) => s.item.inCatalog && s.item.enabled);
  const unreachable = combat.filter((s) => s.unreachable);
  const starved = combat.filter((s) => s.starved);

  const needle = query.trim().toLowerCase();
  const rows = summaries.filter((s) => {
    if (!showDisabled && !(s.item.inCatalog && s.item.enabled)) return false;
    if (onlyProblems && !s.unreachable && !s.starved) return false;
    if (needle === '') return true;
    return (
      s.item.id.toLowerCase().includes(needle) ||
      (s.item.displayName ?? '').toLowerCase().includes(needle) ||
      s.item.tags.some((tag) => tag.toLowerCase().includes(needle))
    );
  });

  const knob = (key: string) => {
    const found = knobByKey.get(key);
    return found === undefined ? null : <KnobInput key={key} knob={found} edits={edits} onEdit={onEdit} />;
  };

  return (
    <div className="page">
      <div className="page-head">
        <h1>Предметы</h1>
        <p>
          Каждый предмет каталога и все его источники: шанс в тайниках (строки таблиц лута, тег-строки
          развёрнуты), ожидание штук за уровень и за акт по назначенным бюджетам, дроп с врагов, адресные
          места, наборы, рецепты и прилавок. Числа можно покрутить — вероятности пересчитываются сразу,
          а перенос в ассеты делается руками по списку справа.
        </p>
        {model && <OriginTag origin={origin} />}
        {model && (
          <span className="dim">
            {' '}
            ассеты прочитаны {stamp(model.generatedAtUtc)} ·{' '}
            <button className="ghost" disabled={loading || !(store.server?.online ?? false)} onClick={() => load(true)}>
              перечитать ассеты
            </button>
          </span>
        )}
      </div>

      {loading && model === null && <div className="empty">читаю ассеты предметов…</div>}
      {!loading && model === null && (
        <Callout tone="hard" title="Модели предметов нет">
          <p>{failure ?? 'Нужен локальный сервер (он читает .asset) или запечённый образец.'}</p>
          <pre className="cmd">Tools/playground.sh</pre>
        </Callout>
      )}
      {model && origin === 'sample' && (
        <Callout title="Образец, а не живые ассеты">
          <p>
            Сервер не ответил{failure ? ` (${failure})` : ''} — показана модель, запечённая при сборке. Правки
            ассетов после сборки здесь не видны.
          </p>
        </Callout>
      )}
      {model && model.warnings.length > 0 && (
        <Callout tone="hard" title="Сервер не смог разобрать часть ссылок">
          {model.warnings.map((warning) => (
            <p key={warning} className="mono">
              {warning}
            </p>
          ))}
        </Callout>
      )}

      {edited && (
        <>
          <div className="tiles">
            <Tile label="Предметов в каталоге" value={num(combat.length)} note={`включённых; всего ассетов ${num(summaries.length)}`} />
            <Tile
              label="Без источника"
              value={num(unreachable.length)}
              tone={unreachable.length === 0 ? 'good' : 'bad'}
              note={unreachable.length === 0 ? 'как отчёт достижимости' : unreachable.map((s) => s.item.id).join(', ')}
            />
            <Tile
              label="Не выпадают при этих числах"
              value={num(starved.length)}
              tone={starved.length === 0 ? 'good' : 'warn'}
              note={starved.length === 0 ? 'источник есть и даёт > 0' : starved.map((s) => s.item.id).join(', ')}
            />
            <Tile
              label="Правок"
              value={num(changes.length)}
              tone={changes.length === 0 ? 'plain' : 'warn'}
              note={changes.length === 0 ? 'числа как в ассетах' : 'перенести руками — список справа'}
            />
          </div>

          {problems.length > 0 && (
            <Callout tone="hard" title="Такие числа валидатор ассетов не пропустит">
              {problems.map((problem) => (
                <p key={problem} className="mono">
                  {problem}
                </p>
              ))}
            </Callout>
          )}

          <div className="items-layout">
            <div className="items-main">
              <Panel
                title={`Предметы — ${num(rows.length)}`}
                flush
                aside={
                  <div className="controls">
                    <label className="field">
                      поиск
                      <input type="text" value={query} placeholder="id, имя, тег" onChange={(e) => setQuery(e.target.value)} />
                    </label>
                    <label className="field">
                      <input type="checkbox" checked={showDisabled} onChange={(e) => setShowDisabled(e.target.checked)} />
                      выключенные и вне каталога
                    </label>
                    <label className="field">
                      <input type="checkbox" checked={onlyProblems} onChange={(e) => setOnlyProblems(e.target.checked)} />
                      только проблемные
                    </label>
                  </div>
                }
              >
                <div className="scroll tall">
                  <table className="items-table">
                    <thead>
                      <tr>
                        <th>Предмет</th>
                        <th>Тайники (акт {model?.productionAct})</th>
                        <th>Дроп с врагов</th>
                        <th>Комнаты и места</th>
                        <th>Прилавок</th>
                        <th className="n">≈ за акт</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((summary) => (
                        <Fragment key={summary.item.asset.path}>
                          <ItemRow summary={summary} open={open === summary.item.asset.path} onToggle={() => setOpen(open === summary.item.asset.path ? null : summary.item.asset.path)} />
                          {open === summary.item.asset.path && <ItemDetail summary={summary} knob={knob} />}
                        </Fragment>
                      ))}
                      {rows.length === 0 && (
                        <tr>
                          <td colSpan={6} className="dim">
                            ничего не подходит под фильтр
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Panel>

              <KnobsPanel model={edited} knob={knob} />
            </div>

            <aside className="items-aside">
              <Panel
                title="Перенести в ассеты"
                aside={
                  <div className="controls">
                    <CopyButton text={formatChanges(changes)} label="копировать" />
                    <button className="ghost" disabled={Object.keys(edits).length === 0} onClick={() => setEdits({})}>
                      сбросить
                    </button>
                  </div>
                }
              >
                {changes.length === 0 ? (
                  <p className="dim small">
                    Правок нет. Поменяйте вес строки, мин/макс бюджета или шанс дропа — в раскрытой строке
                    предмета или в панели «Все ручки».
                  </p>
                ) : (
                  <table className="changes">
                    <thead>
                      <tr>
                        <th>Ассет · поле</th>
                        <th className="n">Было</th>
                        <th className="n">Стало</th>
                      </tr>
                    </thead>
                    <tbody>
                      {changes.map((change) => (
                        <tr key={change.asset.path + change.field} title={change.asset.path}>
                          <td>
                            <div className="mono">{change.asset.name}</div>
                            <div className="mono dim small">
                              {change.field} · {change.label}
                            </div>
                          </td>
                          <td className="n dim">{num(change.from)}</td>
                          <td className="n warn">{num(change.to)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <p className="faint small" style={{ marginBottom: 0 }}>
                  {model?.writePolicy}
                </p>
              </Panel>

              <Panel title="Как считается">
                <ul className="small formulas">
                  <li>
                    <b>Доля в таблице</b> = вес строки / сумма весов. Тег-строка веса W делится поровну между
                    включёнными предметами тега (как LootTableDef.TryToValues). Пустая строка — «ничего».
                  </li>
                  <li>
                    <b>Рабочая таблица</b> категории — самая точная: акт забега &gt; «любой акт». Тематические
                    отложены (ADR-0025 §4) и не срабатывают.
                  </li>
                  <li>
                    <b>За уровень</b> (разброс): среднее находок (мин+макс)/2 × штук в находке × доля. Талоны —
                    штук в находке (стак мин+макс)/2.
                  </li>
                  <li>
                    <b>Патроны</b>: не мин/макс строки, а врагов × выстрелов × ‰ (вниз, строго меньше потребности).
                    Врагов — диапазон бюджета; точное число даёт планировка, т.е. только симуляция.
                  </li>
                  <li>
                    <b>За акт</b> — сумма по уровням 1…N из SourceCatalog (фолбэк — если уровней нет).
                  </li>
                  <li>
                    <b>Дроп</b> = шанс дропа % × доля в таблице дропа, за одно убийство. «За уровень» — если убить
                    всех врагов бюджета.
                  </li>
                  <li>
                    Не учтено: переполнение уровня (верхняя оценка), шкафы с медициной (число от мебели), ключ без
                    замка снимается.
                  </li>
                </ul>
              </Panel>
            </aside>
          </div>
        </>
      )}
    </div>
  );
}

function ItemRow({ summary, open, onToggle }: { summary: ItemSummary; open: boolean; onToggle: () => void }) {
  const { item } = summary;
  const active = summary.stash.filter((line) => line.status === 'active');
  const inactive = summary.stash.filter((line) => line.status !== 'active');
  return (
    <tr className={open ? 'item-row open' : 'item-row'} onClick={onToggle}>
      <td>
        <div>
          <span className="mono">{open ? '▾' : '▸'} {item.id}</span>{' '}
          {summary.unreachable && <span className="tag bad">нет источника</span>}
          {summary.starved && <span className="tag warn">не выпадает</span>}
          {!item.enabled && <span className="tag warn">выключен</span>}
          {item.enabled && !item.inCatalog && <span className="tag warn">вне каталога</span>}
          {item.placeholder && <span className="tag">заглушка</span>}
          {item.sourcedByScript && <span className="tag" title={item.sourcedByScriptNote}>скрипт</span>}
        </div>
        <div className="dim small">{item.displayName ?? '—'}</div>
        {item.tags.length > 0 && <div className="small mono faint">#{item.tags.join(' #')}</div>}
        {summary.aspects.length > 0 && <div className="small">{summary.aspects.join(' · ')}</div>}
      </td>
      <td className="small">
        {active.map((line) => (
          <div key={line.table.asset.path}>
            <span className="mono">{line.table.asset.name}</span>{' '}
            <span className="dim">
              {line.rows.map((row) => row.weight).join('+')}/{line.tableWeight}
              {line.rows.some((row) => row.viaTag !== null) ? ' (тег)' : ''}
            </span>{' '}
            <b>{percent(line.share)}</b>
          </div>
        ))}
        {inactive.map((line) => (
          <div key={line.table.asset.path} className="faint" title={TABLE_STATUS_TEXT[line.status]}>
            <span className="mono">{line.table.asset.name}</span> — {TABLE_STATUS_TEXT[line.status]}
          </div>
        ))}
        {summary.stash.length === 0 && <span className="faint">—</span>}
      </td>
      <td className="small">
        {summary.drops.map((drop) => (
          <div key={drop.enemy.asset.path} className={drop.enemy.assigned ? '' : 'faint'}>
            <span className="mono">{drop.enemy.asset.name}</span> {num(drop.enemy.dropChancePercent)}% × {percent(drop.share)} ={' '}
            <b>{percent(drop.perKill)}</b>
            <span className="dim"> за убийство</span>
            {!drop.enemy.assigned && ' (не назначен)'}
          </div>
        ))}
        {summary.drops.length === 0 && <span className="faint">—</span>}
      </td>
      <td className="small">
        {active.flatMap((line) =>
          line.preferredBy.length > 0
            ? [
                <div key={`pref-${line.table.asset.path}`}>
                  предпочитают «{line.category?.id}»: {line.preferredBy.map((module) => module.id ?? module.asset.name).join(', ')}
                </div>,
              ]
            : [],
        )}
        {active.some((line) => line.furnitureMedicine) && <div className="dim">+ шкафы с медициной</div>}
        {summary.places
          .filter((place) => place.kind !== 'shop')
          .map((place) => (
            <div key={place.asset.path + place.text}>{place.text}</div>
          ))}
        {active.every((line) => line.preferredBy.length === 0) && summary.places.filter((p) => p.kind !== 'shop').length === 0 && (
          <span className="faint">—</span>
        )}
      </td>
      <td className="small">
        {summary.shop.map((entry) => (
          <div key={entry.asset.path} className={entry.onSale ? '' : 'faint'}>
            {entry.onSale ? (
              <>
                <b>{num(entry.priceTalons)}</b> тал. за {num(entry.count)}
              </>
            ) : (
              <>
                цена {num(entry.priceTalons)} · {entry.priceApproved ? 'утв.' : 'не утверждена'} — не продаётся
              </>
            )}
          </div>
        ))}
        {summary.shop.length === 0 && <span className="faint">—</span>}
      </td>
      <td className={summary.stashPerAct.high === 0 ? 'n faint' : 'n'}>{est(summary.stashPerAct, 1)}</td>
    </tr>
  );
}

function ItemDetail({ summary, knob }: { summary: ItemSummary; knob: (key: string) => ReactElement | null }) {
  return (
    <tr className="item-detail">
      <td colSpan={6}>
        {summary.unreachable && (
          <Callout tone="hard" title="Нет ни одного источника">
            <p>
              Как в отчёте достижимости (Validate Content): добавьте строку в таблицу лута, рецепт, набор или
              запись терминала — или включите у предмета «Выдаётся скриптом» с пояснением.
            </p>
          </Callout>
        )}
        {summary.starved && (
          <Callout title="Источник есть, но при этих числах предмет не появляется">
            <p>Ожидание за акт — ноль: проверьте мин/макс строки бюджета его категории.</p>
          </Callout>
        )}

        {summary.stash.map((line) => (
          <div key={line.table.asset.path} className="detail-block">
            <h4>
              <span className="mono">{line.table.asset.name}</span> · категория «{line.table.category ?? '—'}»
              {line.category?.rule ? ` (${line.category.rule.type})` : ''} · акт {line.table.act === 0 ? 'любой' : line.table.act}
              {line.table.theme ? ` · тема ${line.table.theme}` : ''}{' '}
              <span className={line.status === 'active' ? 'tag good' : 'tag warn'}>{TABLE_STATUS_TEXT[line.status]}</span>
            </h4>
            {line.error && <p className="bad mono">{line.error}</p>}
            <div className="controls small">
              веса строк:
              {line.table.entries.map((_, index) => knob(tableKey(line.table.asset.name, index)))}
            </div>
            <p className="small">
              доля «{summary.item.id}» = {line.rows.map((row) => `${row.weight}${row.viaTag ? ` (тег ${row.viaTag})` : ''}`).join(' + ')} из{' '}
              {line.tableWeight} → <b>{percent(line.share, 2)}</b>
              {line.furnitureMedicine && ' · эта же таблица наполняет шкафы с медициной (их число — от мебели, не считается)'}
            </p>
            {line.perBudget.length > 0 && (
              <table>
                <thead>
                  <tr>
                    <th>Бюджет</th>
                    <th>Уровни</th>
                    <th>Строка бюджета</th>
                    <th className="n">≈ штук за уровень</th>
                    <th>Как посчитано</th>
                  </tr>
                </thead>
                <tbody>
                  {line.perBudget.map((entry) => {
                    const rowIndex = entry.budget.lootRows.findIndex((row) => row.category === line.table.category);
                    const isAmmo = line.category?.rule?.type === 'AmmoDeficitLootRule';
                    const name = entry.budget.asset.name;
                    return (
                      <tr key={name + rowIndex}>
                        <td className="mono">{name}</td>
                        <td className="dim">
                          {entry.budget.levels.length > 0 ? entry.budget.levels.join(', ') : ''}
                          {entry.budget.fallback ? (entry.budget.levels.length > 0 ? ' · фолбэк' : 'фолбэк') : ''}
                          {entry.budget.levels.length === 0 && !entry.budget.fallback ? 'не назначен' : ''}
                        </td>
                        <td>
                          <div className="controls small">
                            {isAmmo ? (
                              <>
                                {knob(budgetFieldKey(name, 'minEnemies'))}
                                {knob(budgetFieldKey(name, 'maxEnemies'))}
                                {knob(budgetFieldKey(name, 'averageShotsPerEnemy'))}
                                {knob(budgetFieldKey(name, 'ammoBudgetPermille'))}
                              </>
                            ) : (
                              <>
                                {knob(budgetRowKey(name, rowIndex, 'min'))}
                                {knob(budgetRowKey(name, rowIndex, 'max'))}
                              </>
                            )}
                          </div>
                        </td>
                        <td className={entry.dependsOnLayout ? 'n warn' : 'n'} title={entry.dependsOnLayout ? 'зависит от планировки' : undefined}>
                          {est(entry.expected)}
                          {entry.dependsOnLayout && entry.expected !== null ? ' *' : ''}
                        </td>
                        <td className="small dim">{entry.note}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {line.perAct !== null && (
              <p className="small">
                за акт ({line.actLevels} ур.): <b>{est(line.perAct)}</b>
              </p>
            )}
            {line.preferredBy.length > 0 && (
              <p className="small">
                модули с предпочтением «{line.table.category}» (находка категории ложится туда охотнее, ×бонус
                бюджета): {line.preferredBy.map((module) => module.asset.name).join(', ')}
              </p>
            )}
          </div>
        ))}

        {summary.drops.map((drop) => (
          <div key={drop.enemy.asset.path} className="detail-block">
            <h4>
              Дроп: <span className="mono">{drop.enemy.asset.name}</span>
              {drop.viaFallback ? ' (своей таблицы нет — запасная категория)' : ''} · таблица{' '}
              <span className="mono">{drop.table?.asset.name}</span>
              {!drop.enemy.assigned && <span className="tag warn">не назначен в SourceCatalog — по уровням не ходит</span>}
            </h4>
            <div className="controls small">{knob(enemyKey(drop.enemy.asset.name))}</div>
            <p className="small">
              {num(drop.enemy.dropChancePercent)}% × {percent(drop.share, 2)} = <b>{percent(drop.perKill, 2)}</b> с одного убийства
            </p>
            {drop.perBudget.length > 0 && (
              <p className="small">
                если убить всех:{' '}
                {drop.perBudget.map((entry) => `${entry.budget.asset.name} ≈ ${est(entry.expected)}`).join(' · ')}
              </p>
            )}
          </div>
        ))}

        {summary.places.length > 0 && (
          <div className="detail-block">
            <h4>Адресные места, наборы, рецепты, прилавок</h4>
            {summary.places.map((place) => (
              <p key={place.asset.path + place.text} className="small">
                <span className="mono">{place.asset.name}</span> — {place.text}
                {!place.source && <span className="faint"> (источником не считается)</span>}
              </p>
            ))}
          </div>
        )}

        <p className="small dim">
          источники по отчёту достижимости: {summary.sources.length > 0 ? summary.sources.join(', ') : 'нет'}
          {summary.item.sourcedByScript ? ` · выдаётся скриптом: ${summary.item.sourcedByScriptNote}` : ''}
        </p>
        <p className="small faint">{summary.item.asset.path}</p>
      </td>
    </tr>
  );
}

function KnobsPanel({ model, knob }: { model: ItemsPayload; knob: (key: string) => ReactElement | null }) {
  const rules = new Map(model.categories.map((category) => [category.id, category.rule?.type ?? null]));
  return (
    <Panel title="Все ручки">
      <details>
        <summary className="dim">таблицы лута, строки бюджетов, шанс дропа — всё, что крутится во вкладке</summary>
        <div className="split" style={{ marginTop: 10 }}>
          <div>
            <h4>Таблицы лута</h4>
            {model.tables.map((table) => (
              <div key={table.asset.path} className="knob-group">
                <div className="mono small">
                  {table.asset.name} <span className="faint">«{table.category ?? '—'}», акт {table.act === 0 ? 'любой' : table.act}</span>
                </div>
                <div className="controls small">{table.entries.map((_, index) => knob(tableKey(table.asset.name, index)))}</div>
              </div>
            ))}
          </div>
          <div>
            <h4>Бюджеты уровней</h4>
            {model.budgets.map((budget) => (
              <div key={budget.asset.path} className="knob-group">
                <div className="mono small">
                  {budget.asset.name}{' '}
                  <span className="faint">
                    {budget.levels.length > 0 ? `уровни ${budget.levels.join(', ')}` : ''}
                    {budget.fallback ? ' фолбэк' : ''}
                  </span>
                </div>
                <div className="controls small">
                  {budget.lootRows.map((row, index) =>
                    row.category !== null && rules.get(row.category) === 'AmmoDeficitLootRule' ? null : (
                      <Fragment key={index}>
                        {knob(budgetRowKey(budget.asset.name, index, 'min'))}
                        {knob(budgetRowKey(budget.asset.name, index, 'max'))}
                      </Fragment>
                    ),
                  )}
                </div>
                <div className="controls small">
                  {knob(budgetFieldKey(budget.asset.name, 'minEnemies'))}
                  {knob(budgetFieldKey(budget.asset.name, 'maxEnemies'))}
                  {knob(budgetFieldKey(budget.asset.name, 'averageShotsPerEnemy'))}
                  {knob(budgetFieldKey(budget.asset.name, 'ammoBudgetPermille'))}
                </div>
              </div>
            ))}
          </div>
          <div>
            <h4>Враги</h4>
            {model.enemies.map((enemy) => (
              <div key={enemy.asset.path} className="knob-group">
                <div className="mono small">
                  {enemy.asset.name} <span className="faint">таблица {enemy.dropTable ?? 'запасная'}{enemy.assigned ? '' : ' · не назначен'}</span>
                </div>
                <div className="controls small">{knob(enemyKey(enemy.asset.name))}</div>
              </div>
            ))}
          </div>
        </div>
      </details>
    </Panel>
  );
}
